/* eslint-disable complexity */
import type {PressReleasePreview} from '@openapi/press-release';
import type {
  CompanyDetail,
  PressReleasesByCompanyIdDataInner,
  StoriesByCompanyIdDataInner,
} from '@openapi/prtimes';
import {breadCrumb} from '@press-release-ui/bread-crumb';
import {companyInformation} from '@press-release-ui/company-information';
import {imageList} from '@press-release-ui/image-list';
import {pressReleaseBody} from '@press-release-ui/press-release-body';
import {pressReleaseLayout} from '@press-release-ui/press-release-layout';
import {latestPressReleases} from '@press-release-ui/latest-press-releases';
import {latestStories} from '@press-release-ui/latest-stories';
import {pressReleaseHeader} from '@press-release-ui/press-release-header';
import {snsShareButtons} from '@press-release-ui/sns-share-buttons';
import {pressReleaseInformation} from '@press-release-ui/press-release-information';
import {mediaUserOnlyInfo} from '@press-release-ui/media-user-only-info';
import {pressReleaseCap} from '@press-release-ui/press-release-cap';
import {colors} from '@prtimes-basis/unfixed-new-ui/tokens';
import {
  jsCompanyInformationId,
  jsLatestPressReleases,
  jsLatestStories,
} from '@press-release-ui/lib/const';
import type {PreviewDeviceType} from '../../hooks/use-preview-device-state';
import {formatIsoStringToJp} from '../../lib/format-iso-string-to-jp';
import {composeImageUrl} from '../../lib/compose-image-url';
import {PcFooter} from '../web-preview-mocks/pc-footer';
import {PcHeader} from '../web-preview-mocks/pc-header';
import {SpFooter} from '../web-preview-mocks/sp-footer';
import {SpHeader} from '../web-preview-mocks/sp-header';
import {renderLinkifiedText} from '../../lib/render-linkified-text';
import {usePressReleaseImagesWithoutPixta} from '../../lib/use-press-release-images-without-pixta';
import {fileProps, isAllFileNull} from './file-props';
import {useApplyTableAnimationEffect} from './effects/use-apply-table-animation-effect';
import {useDecoratePixtaEffect} from './effects/use-decorate-pixta-effect';
import styles from './web-preview.module.css';

type Props = {
  readonly device: PreviewDeviceType;
  readonly pressRelease: PressReleasePreview;
  readonly company: CompanyDetail;
  readonly latestPressReleasesData?: PressReleasesByCompanyIdDataInner[];
  readonly latestStoriesData?: StoriesByCompanyIdDataInner[];
};

export function WebPreview({
  device,
  pressRelease,
  company,
  latestPressReleasesData,
  latestStoriesData,
}: Props) {
  const Header = device === 'mobile' ? SpHeader : PcHeader;
  const Footer = device === 'mobile' ? SpFooter : PcFooter;
  const isShow = {
    downloadLink: pressRelease.isShowDownloadLink,
    releaseDocs: !isAllFileNull(fileProps(pressRelease)),
  };
  const {images: releaseImages} = usePressReleaseImagesWithoutPixta();
  const releaseCompleDateJp =
    pressRelease.releaseCompleDate === null
      ? '配信時間未定'
      : formatIsoStringToJp(pressRelease.releaseCompleDate);

  const isOldRelease = pressRelease.editorVersion === null;
  useApplyTableAnimationEffect(isOldRelease);

  useDecoratePixtaEffect();

  function PressReleaseHeaderCompounded() {
    return (
      <pressReleaseHeader.root isPreview>
        <pressReleaseHeader.companyName
          companyName={pressRelease.companyName}
          companyId={pressRelease.companyId}
        />
        <pressReleaseHeader.right>
          <pressReleaseHeader.responseiveArea>
            <pressReleaseHeader.internalLink anchor={jsCompanyInformationId}>
              会社概要
            </pressReleaseHeader.internalLink>
            {latestPressReleasesData === undefined ||
              (latestPressReleasesData.length > 0 && (
                <pressReleaseHeader.internalLink anchor={jsLatestPressReleases}>
                  プレスリリース
                </pressReleaseHeader.internalLink>
              ))}
            {latestStoriesData === undefined ||
              (latestStoriesData.length > 0 && (
                <pressReleaseHeader.internalLink anchor={jsLatestStories}>
                  ストーリー
                </pressReleaseHeader.internalLink>
              ))}
          </pressReleaseHeader.responseiveArea>

          {company.supporterFlg ? (
            <pressReleaseHeader.followButtonReadonly />
          ) : null}
        </pressReleaseHeader.right>
      </pressReleaseHeader.root>
    );
  }

  function RelatedArticles() {
    return (
      <>
        {latestPressReleasesData !== undefined &&
          latestPressReleasesData.length > 0 && (
            <latestPressReleases.root>
              <latestPressReleases.title companyId={pressRelease.companyId} />
              <latestPressReleases.carousel
                pressReleases={latestPressReleasesData}
              />
            </latestPressReleases.root>
          )}
        {latestStoriesData !== undefined && latestStoriesData.length > 0 && (
          <latestStories.root>
            <latestStories.title companyId={pressRelease.companyId} />
            <latestStories.carousel stories={latestStoriesData} />
          </latestStories.root>
        )}
      </>
    );
  }

  const rootStyle = {
    backgroundColor: colors.white,
    ...(device === 'mobile'
      ? {marginInline: 'auto', width: '390px'}
      : {paddingTop: 'auto'}),
  };

  return (
    <div className={styles.container}>
      <Header />
      <pressReleaseLayout.root
        version={pressRelease.editorVersion ?? '2015'}
        style={rootStyle}
      >
        <PressReleaseHeaderCompounded />
        <pressReleaseLayout.main>
          <pressReleaseLayout.pressReleaseBox>
            <pressReleaseCap.root>
              <pressReleaseCap.titles>
                <pressReleaseCap.title title={pressRelease.title} />
                <pressReleaseCap.subtitle>
                  {renderLinkifiedText(pressRelease.subtitle)}
                </pressReleaseCap.subtitle>
              </pressReleaseCap.titles>
              <pressReleaseCap.meta>
                <pressReleaseCap.companyName
                  companyName={company.companyName}
                  companyId={pressRelease.companyId}
                />
                <pressReleaseCap.bottom>
                  {pressRelease.releaseCompleDate === null ? (
                    <pressReleaseCap.releaseDatePending />
                  ) : (
                    <pressReleaseCap.releaseDate
                      dateJp={releaseCompleDateJp}
                      date={pressRelease.releaseCompleDate}
                    />
                  )}
                  <snsShareButtons.root>
                    <snsShareButtons.likeButtonReadonly />
                    <snsShareButtons.clipButtonReadonly />
                    <snsShareButtons.divider />
                    {!pressRelease.releaseTargetFlgMediaOnly && (
                      <>
                        <snsShareButtons.xShareButtonReadonly />
                        <snsShareButtons.facebookShareButtonReadonly />
                        <snsShareButtons.lineShareButtonReadonly />
                        <snsShareButtons.divider />
                      </>
                    )}
                    {pressRelease.isShowDownloadLink ? (
                      <snsShareButtons.downloadButtonReadonly />
                    ) : null}
                    <snsShareButtons.copyLinkButtonReadonly />
                  </snsShareButtons.root>
                </pressReleaseCap.bottom>
              </pressReleaseCap.meta>
            </pressReleaseCap.root>
            <pressReleaseLayout.pressReleaseMain>
              <pressReleaseBody.root>
                {pressRelease.releaseTargetFlgMediaOnly ? (
                  <pressReleaseBody.mediaUserOnlyNotice />
                ) : null}
                {pressRelease.head !== '' && (
                  <pressReleaseBody.leadParagraph>
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{__html: pressRelease.head}}
                    />
                  </pressReleaseBody.leadParagraph>
                )}
                <pressReleaseBody.text
                  isSp={device === 'mobile'}
                  textHtml={pressRelease.text}
                />
                {pressRelease.mediaUserOnlyInfo !== null && (
                  <mediaUserOnlyInfo.root>
                    {!pressRelease.releaseTargetFlgMediaOnly && (
                      <mediaUserOnlyInfo.notice />
                    )}
                    <mediaUserOnlyInfo.content>
                      {renderLinkifiedText(pressRelease.mediaUserOnlyInfo)}
                    </mediaUserOnlyInfo.content>
                  </mediaUserOnlyInfo.root>
                )}
                {releaseImages.length > 0 && (
                  <imageList.root imagesLength={releaseImages.length}>
                    <imageList.top>
                      <imageList.heading />
                      <imageList.dotIndicators />
                    </imageList.top>
                    <imageList.backButton />
                    <imageList.list>
                      {releaseImages.map((image) => (
                        <imageList.item
                          key={image.fileName}
                          src={composeImageUrl(
                            pressRelease.companyId,
                            pressRelease.releaseId,
                            image,
                            1440,
                          )}
                          triggerSrc={composeImageUrl(
                            pressRelease.companyId,
                            pressRelease.releaseId,
                            image,
                            720,
                          )}
                          alt={image.caption}
                        />
                      ))}
                    </imageList.list>
                    <imageList.forwardButton />
                  </imageList.root>
                )}
              </pressReleaseBody.root>
              <pressReleaseLayout.pressReleaseMainDivider />
              <pressReleaseInformation.root>
                <pressReleaseInformation.table
                  releaseTypeId={pressRelease.releaseTypeId}
                  releaseTypeName={pressRelease.releaseTypeName}
                  businessCategories={pressRelease.businessCategories}
                  locations={pressRelease.locations}
                  keywords={pressRelease.keywords}
                  referenceUrl={pressRelease.referenceUrl}
                  renderDownload={() => (
                    <pressReleaseInformation.downloadRow
                      isShow={{...isShow}}
                      companyId={pressRelease.companyId}
                      companyName={company.companyName}
                      releaseId={pressRelease.releaseId}
                      {...fileProps(pressRelease)}
                    />
                  )}
                  renderPressKitLink={() =>
                    company.isPressKitPublic &&
                    company.pressKitItemCount > 0 ? (
                      <pressReleaseInformation.pressKitLink
                        companyId={company.companyId}
                        companyName={company.companyName}
                      />
                    ) : null
                  }
                />
              </pressReleaseInformation.root>
            </pressReleaseLayout.pressReleaseMain>
          </pressReleaseLayout.pressReleaseBox>
          <breadCrumb.root>
            <breadCrumb.list>
              <breadCrumb.item href='/'>トップ</breadCrumb.item>
              <breadCrumb.item href='/main/html/index/pagenum/1'>
                プレスリリース
              </breadCrumb.item>
              <breadCrumb.item
                href={`/main/html/searchrlp/company_id/${pressRelease.companyId}`}
              >
                {pressRelease.companyName}
              </breadCrumb.item>
              <breadCrumb.item>{pressRelease.title}</breadCrumb.item>
            </breadCrumb.list>
          </breadCrumb.root>
          <pressReleaseLayout.bottoms>
            <companyInformation.root>
              <companyInformation.title />
              <companyInformation.body>
                <companyInformation.overview>
                  {company.logoImageUrl?.pc !== undefined && (
                    <companyInformation.companyLogo
                      src={company.logoImageUrl.pc}
                      companyId={pressRelease.companyId}
                    />
                  )}
                  <companyInformation.companyName
                    companyName={pressRelease.companyName}
                    companyId={pressRelease.companyId}
                  />
                  {company.supporterFlg ? (
                    <>
                      <companyInformation.followerCount
                        value={company.followerNum}
                      />
                      <companyInformation.followButtonReadonly />
                    </>
                  ) : null}
                  <companyInformation.rssLink
                    companyId={pressRelease.companyId}
                  />
                </companyInformation.overview>
                <companyInformation.table
                  url={company.url}
                  industryKbnName={company.industryKbnName}
                  address={
                    (company.address || '') +
                      (company.address2 ? `\n${company.address2}` : '') || '-'
                  }
                  phone={company.phone}
                  presidentName={company.presidentName}
                  ipoKbnName={company.ipoKbnName}
                  capital={company.capital ?? ''}
                  foundationDate={
                    company.foundationDate === null
                      ? ''
                      : `${company.foundationDate.year}年${company.foundationDate.month}月`
                  }
                />
              </companyInformation.body>
              <companyInformation.bottom>
                <companyInformation.documentBanner />
              </companyInformation.bottom>
            </companyInformation.root>
            <RelatedArticles />
          </pressReleaseLayout.bottoms>
        </pressReleaseLayout.main>
      </pressReleaseLayout.root>

      <Footer />
    </div>
  );
}
